import React from 'react';
import { Accordion, Table } from 'react-bootstrap';
import DataImporter from './index';
import { createBillOfMaterials } from '../../api/bill-of-materialsAPI';
import { createParts, getPartByNumberAndSource } from '../../api/partsAPI';
import handleDownloadTemplateHelper from './handleDownloadTemplateHelper';
import { useSetting } from '../../utils/AppSettingsContext';

const DEFAULT_PART_CATEGORY_ID = 25;

const truncateToTwoDecimals = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return Math.trunc(numericValue * 100) / 100;
};

const BillOfMaterialsImporter = ({ onComplete }) => {
  const defaultExpenseParts = useSetting('default_expense_parts');

  const handleDataImport = async (rows, onProgress) => {
    let count = 0;
    const defaultExpenseCodeId = Number(defaultExpenseParts);

    for (const row of rows) {
      try {
        const partNumber = String(row.partNumber || '').trim();
        const partDescription = String(row.partDescription || '').trim();
        const sourceOfSupplyId = row.partSourceOfSupply
          ? Number(row.partSourceOfSupply)
          : null;
        const supplierId = row.partSupplier ? Number(row.partSupplier) : null;
        const partCategoryId = row.partCategory
          ? Number(row.partCategory)
          : DEFAULT_PART_CATEGORY_ID;
        const partsPriceValue = truncateToTwoDecimals(row.partsPrice);

        let partId;
        let existingPart = null;

        if (sourceOfSupplyId) {
          existingPart = await getPartByNumberAndSource({
            partNumber,
            sourceOfSupplyId,
          });
        }

        if (existingPart?.id) {
          partId = existingPart.id;
        } else {
          if (!sourceOfSupplyId || !supplierId) {
            console.error(
              'Skipping bill of materials row: source of supply and supplier are required for new parts.',
              row
            );
            count += 1;
            if (onProgress) onProgress(count);
            continue;
          }

          const createdPart = await createParts({
            partNumber,
            oemNumber: partNumber,
            sourceOfSupplyId,
            categoryId: partCategoryId,
            description: partDescription,
            supplierId,
            lastPrice: partsPriceValue ?? 0,
            pricingDate: new Date(),
          });
          partId = createdPart.id;
        }

        const resolvedExpenseCode =
          row.defaultExpenseCode ??
          (Number.isFinite(defaultExpenseCodeId) ? defaultExpenseCodeId : undefined);

        const createBom = await createBillOfMaterials({
          taskId: row.taskId,
          partsId: partId,
          optional: Number(row.optional) || 0,
          quantity: Number(row.quantity) || 0,
          defaultExpenseCode: resolvedExpenseCode,
          partsPrice: partsPriceValue ?? 0,
          pricingDate: new Date(),
        });

      } catch (err) {
        console.error('Failed to import bill of materials row', err);
      }
      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          You can bulk import <strong>bill of materials</strong> into the system using an Excel or
          CSV file. This helps speed up onboarding or migration processes by avoiding manual entry.
        </p>
        <p>Expand the lists below to see the required and optional fields.</p>
        <div className='mb-4'>
          <button
            className='btn btn-outline-secondary btn-sm'
            onClick={() => handleDownloadTemplateHelper('billofmaterials')}
          >
            Download Template
          </button>
        </div>
        <div className='mb-4'>
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>assetId</td>
                      <td>Lookup</td>
                      <td>Asset number used to resolve the asset ID</td>
                    </tr>
                    <tr>
                      <td>taskId</td>
                      <td>Lookup</td>
                      <td>Task name for the selected asset</td>
                    </tr>
                    <tr>
                      <td>quantity</td>
                      <td>Number</td>
                      <td>Whole number greater than or equal to 0</td>
                    </tr>
                    <tr>
                      <td>partNumber</td>
                      <td>Text</td>
                      <td>Part number</td>
                    </tr>
                    <tr>
                      <td>partDescription</td>
                      <td>Text</td>
                      <td>Description of the part</td>
                    </tr>
                    <tr>
                      <td>partSourceOfSupply</td>
                      <td>Lookup</td>
                      <td>Manufacturer name (required for new parts)</td>
                    </tr>
                    <tr>
                      <td>partSupplier</td>
                      <td>Lookup</td>
                      <td>Supplier name (required for new parts)</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey='1'>
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>optional</td>
                      <td>Number</td>
                      <td>1 if optional, otherwise 0</td>
                    </tr>
                    <tr>
                      <td>partsPrice</td>
                      <td>Number</td>
                      <td>Part price (up to 2 decimal places)</td>
                    </tr>
                    <tr>
                      <td>partCategory</td>
                      <td>Lookup</td>
                      <td>Part category name</td>
                    </tr>
                    <tr>
                      <td>defaultExpenseCode</td>
                      <td>Lookup</td>
                      <td>Expense code name</td>
                    </tr>
                    <tr>
                      <td>pricingDate</td>
                      <td>Date</td>
                      <td>Optional pricing date for the input file</td>
                    </tr>
                    <tr>
                      <td>currency</td>
                      <td>Lookup</td>
                      <td>Currency symbol</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity='bill-of-materials'
          requiredFields={[
            { key: 'assetId', type: 'lookup', required: true, api: 'assets', nameField: 'assetNumber' },
            { key: 'taskId', type: 'lookup', required: true, api: 'tasks', nameField: 'name' },
            { key: 'quantity', type: 'number', required: true },
            { key: 'partNumber', type: 'string', required: true },
            { key: 'partDescription', type: 'string', required: true },
            {
              key: 'partSourceOfSupply',
              type: 'lookup',
              api: 'manufacturers',
              nameField: 'manufacturer',
              required: true,
            },
            {
              key: 'partSupplier',
              type: 'lookup',
              api: 'suppliers',
              nameField: 'name',
              required: true,
            },
            {
              key: 'partCategory',
              type: 'lookup',
              api: 'parts-category',
              nameField: 'name',
            },
            {
              key: 'defaultExpenseCode',
              type: 'lookup',
              api: 'expense-codes',
              nameField: 'name',
            },
            {
              key: 'currency',
              type: 'lookup',
              api: 'currency',
              nameField: 'symbol',
            },
            { key: 'optional', type: 'number' },
            { key: 'partsPrice', type: 'number' },
            { key: 'pricingDate', type: 'date' },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />
      </div>
    </>
  );
};

export default BillOfMaterialsImporter;
